import React, { useState, useRef, useEffect } from "react";
import { FaCheck, FaChevronDown } from "react-icons/fa";

interface PlanOption {
 value: string;
 label: string;
 description?: string;
 price?: string;
 features?: string[];
}

interface PlanSelectorProps {
 value: string;
 onChange: (value: string) => void;
 options: PlanOption[];
 className?: string;
}

export const PlanSelector: React.FC<PlanSelectorProps> = ({
 value,
 onChange,
 options,
 className = "",
}) => {
 const [isOpen, setIsOpen] = useState(false);
 const dropdownRef = useRef<HTMLDivElement>(null);

 useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
   if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
    setIsOpen(false);
   }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
   document.removeEventListener("mousedown", handleClickOutside);
  };
 }, []);

 const selectedOption = options.find((opt) => opt.value === value) || options[0];

 return (
  <div className={`relative ${className}`} ref={dropdownRef}>
   <button
    type="button"
    onClick={() => setIsOpen(!isOpen)}
    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between hover:border-blue-400 transition-colors"
   >
    <div className="flex-1">
     <div className="font-semibold text-gray-900">{selectedOption.label}</div>
     {selectedOption.description && (
      <div className="text-sm text-gray-500 mt-0.5">{selectedOption.description}</div>
     )}
    </div>
    <FaChevronDown
     className={`ml-2 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
    />
   </button>

   {isOpen && (
    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200  overflow-hidden">
     <div className="max-h-96 overflow-y-auto">
      {options.map((option) => (
       <button
        key={option.value}
        type="button"
        onClick={() => {
         onChange(option.value);
         setIsOpen(false);
        }}
        className={`w-full px-4 py-4 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
         value === option.value ? "bg-blue-50" : ""
        }`}
       >
        <div className="flex items-start justify-between">
         <div className="flex-1">
          <div className="flex items-center gap-2">
           <span className="font-semibold text-gray-900">{option.label}</span>
           {value === option.value && (
            <FaCheck className="text-blue-600 text-sm" />
           )}
          </div>
          {option.description && (
           <div className="text-sm text-gray-600 mt-1">{option.description}</div>
          )}
          {option.price && (
           <div className="text-sm font-medium text-blue-600 mt-1">{option.price}</div>
          )}
          {option.features && option.features.length > 0 && (
           <ul className="mt-2 space-y-1">
            {option.features.slice(0, 3).map((feature, idx) => (
             <li key={idx} className="text-xs text-gray-500 flex items-center gap-1">
              <FaCheck className="text-blue-500 text-xs" />
              {feature}
             </li>
            ))}
           </ul>
          )}
         </div>
        </div>
       </button>
      ))}
     </div>
    </div>
   )}
  </div>
 );
};

